/**
 * MoveTaskDialog (FE-5 §6.4)
 *
 * Dialog per spostare un task in modo accessibile (alternativa al drag).
 *
 * Due modalità:
 *  - mode="project": select Progetto libera + select Colonna dipendente.
 *    Usata per "Sposta in un altro progetto".
 *  - mode="column": progetto corrente fisso, select Colonna libera.
 *    Usata per "Sposta nella colonna...".
 *
 * Entrambe prevedono la destinazione "Task senza progetto" (inbox):
 *  - project=null → status=null, select Colonna nascosta.
 *
 * Il submit è disabilitato finché la destinazione non è valida (diversa
 * dall'origine). L'errore backend viene normalizzato inline e NON chiude
 * il dialog. Il toast è responsabilità del caller (che ha il contesto).
 *
 * Il componente è controllato su `open`/`onOpenChange`.
 */

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import type { Task, TaskStatusDetail } from "../../utils/taskHelpers";
import type { TaskProject } from "../../types/tasks";
import { getTaskStatuses } from "../../services/tasks";

export type MoveTaskMode = "project" | "column";

/**
 * Payload restituito al caller. Coerenza FE-4:
 *  - inbox (project=null) → status=null
 *  - progetto → entrambi valorizzati
 */
export interface MoveTaskPayload {
  project: number | null;
  status: number | null;
}

export interface MoveTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Task da spostare (deve contenere project/status correnti). */
  task: Task | null;
  /** Progetti attivi per la select (in mode="project"). */
  availableProjects?: TaskProject[];
  /**
   * - "project": sposta in un altro progetto (select progetto abilitata);
   * - "column": sposta in un'altra colonna dello stesso progetto (select progetto disabilitata).
   */
  mode?: MoveTaskMode;
  /**
   * Progetto corrente da precompilare (default: task.project o task.project_detail.id).
   * In mode="column" la select è disabilitata su questo valore.
   */
  currentProjectId?: number | null;
  /** Submit. Solleva errore testuale per mostrarlo inline (non chiude il dialog). */
  onSubmit: (payload: MoveTaskPayload) => Promise<void>;
}

/**
 * Normalizza un errore backend in messaggio utente.
 * Riconosce `code`/`detail`/campi DRF classici.
 */
export function describeMoveError(err: unknown): string {
  const anyErr = err as any;
  const data = anyErr?.response?.data ?? anyErr;
  if (typeof data === "string") return data;
  if (data?.code === "status_project_mismatch")
    return "La colonna selezionata non appartiene al progetto.";
  if (data?.code === "project_archived")
    return "Il progetto di destinazione è archiviato.";
  if (data?.code === "status_inactive")
    return "La colonna di destinazione non è più utilizzabile.";
  if (data?.code === "task_context_changed")
    return "Il task è stato modificato da un altro client. Ricarica e riprova.";
  if (data?.detail) return String(data.detail);
  if (data && typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
      const val = data[firstKey];
      const msg = Array.isArray(val) ? val[0] : val;
      return `${firstKey}: ${msg}`;
    }
  }
  return "Non è stato possibile spostare il task. Riprova.";
}

const INBOX_VALUE = "__inbox__";

export default function MoveTaskDialog({
  open,
  onOpenChange,
  task,
  availableProjects = [],
  mode = "project",
  currentProjectId,
  onSubmit,
}: MoveTaskDialogProps) {
  // Risolvi progetto corrente: prop esplicita > task.project > project_detail.id > null.
  const resolvedCurrentProject = useMemo(() => {
    if (currentProjectId !== undefined) return currentProjectId;
    if (task == null) return null;
    if (typeof task.project === "number") return task.project;
    if (typeof task.project_detail?.id === "number") return task.project_detail.id;
    return null;
  }, [currentProjectId, task]);

  // selectedProjectId === null  → inbox (status null)
  // selectedProjectId === number → progetto specifico (colonna caricata dal BE)
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [projectColumns, setProjectColumns] = useState<TaskStatusDetail[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset / prefill all'apertura.
  useEffect(() => {
    if (!open) return;
    setError(null);
    // Prefill: progetto corrente del task. La colonna di partenza NON viene
    // preselezionata (l'utente sta cercando una destinazione diversa).
    setSelectedProjectId(resolvedCurrentProject);
    setSelectedStatusId(null);
    setProjectColumns([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  // Carica colonne del progetto selezionato (FE-4 §6.5 stessa logica del modal).
  useEffect(() => {
    if (!open) return;
    if (selectedProjectId === null) {
      setProjectColumns([]);
      setSelectedStatusId(null);
      return;
    }
    let cancelled = false;
    setLoadingColumns(true);
    getTaskStatuses(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const active = data
          .filter((s: TaskStatusDetail) => s && s.is_active)
          .sort((a: TaskStatusDetail, b: TaskStatusDetail) => (a.order ?? 0) - (b.order ?? 0));
        setProjectColumns(active);
        // Default: prima colonna attiva o is_default.
        const def = active.find((c) => c.is_default);
        setSelectedStatusId(def ? def.id : (active[0]?.id ?? null));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Errore caricamento colonne progetto (MoveTaskDialog):", err);
        setProjectColumns([]);
        setSelectedStatusId(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingColumns(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedProjectId]);

  // Destinazione valida: cambia qualcosa rispetto all'origine?
  const sourceProjectId = resolvedCurrentProject;
  const sourceStatusId =
    task?.status_detail?.id ?? task?.status?.id ?? null;

  const isUnchanged =
    selectedProjectId === sourceProjectId &&
    (selectedProjectId === null || selectedStatusId === sourceStatusId);

  const isValidDestination =
    selectedProjectId === null
      ? sourceProjectId !== null // inbox è valido solo se origine non era inbox
      : selectedStatusId != null && !isUnchanged;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidDestination || submitting) return;
    setError(null);
    setSubmitting(true);
    const payload: MoveTaskPayload = {
      project: selectedProjectId,
      status: selectedProjectId === null ? null : selectedStatusId,
    };
    try {
      await onSubmit(payload);
      // Successo: chiude. Il caller può mostrare toast.
      onOpenChange(false);
    } catch (err) {
      setError(describeMoveError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const projectSelectDisabled = mode === "column" || submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)] sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-[var(--hexa-text-primary)]">
            {mode === "project" ? "Sposta in un altro progetto" : "Sposta nella colonna"}
          </DialogTitle>
          <DialogDescription className="text-[var(--hexa-text-secondary)]">
            {task ? (
              <>
                Task: <strong className="text-[var(--hexa-text-primary)]">{task.title}</strong>
              </>
            ) : (
              "Seleziona una destinazione."
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Select Progetto */}
          <div>
            <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
              Progetto di destinazione
            </label>
            <select
              value={selectedProjectId === null ? INBOX_VALUE : String(selectedProjectId)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === INBOX_VALUE) {
                  setSelectedProjectId(null);
                } else {
                  const pid = Number(v);
                  setSelectedProjectId(Number.isFinite(pid) ? pid : null);
                }
                setSelectedStatusId(null);
              }}
              disabled={projectSelectDisabled}
              className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--hexa-purple)] transition disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Progetto di destinazione"
            >
              <option value={INBOX_VALUE}>Task senza progetto (inbox)</option>
              {availableProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {mode === "column" && (
              <p className="text-[10px] text-[var(--hexa-text-muted)] mt-1">
                Stai spostando all'interno dello stesso progetto.
              </p>
            )}
          </div>

          {/* Select Colonna: visibile solo se è selezionato un progetto. */}
          {selectedProjectId !== null && (
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-1">
                Colonna <span className="text-[#ff6467]">*</span>
              </label>
              <select
                value={selectedStatusId ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedStatusId(v === "" ? null : Number(v));
                }}
                disabled={loadingColumns || projectColumns.length === 0 || submitting}
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--hexa-purple)] transition disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Colonna di destinazione"
              >
                {loadingColumns && <option value="">Caricamento colonne...</option>}
                {!loadingColumns && projectColumns.length === 0 && (
                  <option value="">Nessuna colonna disponibile</option>
                )}
                {!loadingColumns &&
                  projectColumns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Errore inline */}
          {error && (
            <p className="text-xs text-[#ef4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={!isValidDestination || submitting}
              className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? "Spostamento..." : "Sposta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
